import {
  CONTINUE_LABEL,
  FINISH_IT_LABEL,
  SHARE_WITH_PROVIDER_LABEL,
  type TSelectableEntityType,
} from '@verifime/components';
import type { TEntityPlanTarget, TPlanIdentity } from './entityPlan';
import { isSubmitted, rowStatus, type TEntityPick } from './entityPlanIdentities';

/**
 * The confirm screen's primary action: what it grants to the client, then where the customer goes.
 * Pure. Granting is the caller's (`POST /v1/permission`, after the confirmation).
 */
export type TConfirmAction = {
  /** The customer's own identities to share with the client, in order. May be empty. */
  shareCustomerIds: string[];
  /**
   * What follows the shares: a wizard to start, a Draft to resume, the end screen after a share,
   * or nothing (back Home).
   */
  then:
    | TEntityPlanTarget
    | { kind: 'resume-draft'; entityType: TSelectableEntityType; identityId: string }
    | { kind: 'resume-individual-draft'; identityId: string }
    | 'shared-end';
};

/**
 * The primary label when there is nothing left to start: the customer has finished here, so the
 * action returns them Home. Same wording as the portal's other return-to-Home buttons.
 */
export const NOTHING_TO_START_LABEL = 'Back to home';

type TActionFacts = {
  individual: TPlanIdentity | undefined;
  /** The entity the customer chose; undefined when the plan is for just me. */
  pick: TEntityPick | undefined;
  /** The identities already shared with this client; undefined when that was not read. */
  sharedIdentityIds: ReadonlySet<string> | undefined;
};

/**
 * The ids to share: a held, submitted Individual, and a chosen held entity, each only when it is
 * known not to be shared with this client already (#609 Q7). When it is not known which are
 * shared, nothing is shared: a grant is never made on a guess.
 */
function idsToShare({ individual, pick, sharedIdentityIds }: TActionFacts): string[] {
  const candidates = [individual?.id, pick?.kind === 'held' ? pick.identityId : undefined];
  return candidates.filter(
    (id): id is string => !!id && !!sharedIdentityIds && !sharedIdentityIds.has(id),
  );
}

/** What follows the shares, from the entity chosen. */
function nextStep(pick: TEntityPick | undefined, shareCount: number): TConfirmAction['then'] {
  switch (pick?.kind) {
    case 'new':
      return pick.entityType;
    case 'draft':
      return { kind: 'resume-draft', entityType: pick.entityType, identityId: pick.identityId };
    default:
      return shareCount > 0 ? 'shared-end' : 'nothing-to-start';
  }
}

/**
 * The Individual's own step while it is not submitted. A Draft with an id is resumed by that id,
 * so the wizard updates it and does not start a second Individual (person-api refuses one). Any
 * other unsubmitted Individual, with no id to resume, starts the wizard. Shares nothing: a Draft
 * cannot be shared.
 */
function individualFirst(individual: TPlanIdentity | undefined): TConfirmAction {
  const draftId = rowStatus(individual) === 'draft' ? individual?.id : undefined;
  return {
    shareCustomerIds: [],
    then: draftId ? { kind: 'resume-individual-draft', identityId: draftId } : 'individual',
  };
}

/**
 * The action for the plan. An Individual not yet submitted comes first and shares nothing (it
 * cannot be shared before it exists): a Draft with an id is resumed, else its wizard starts.
 * Otherwise the Individual is shared as it is, with a chosen held entity, and then the customer
 * starts a new entity's wizard, resumes a chosen Draft, or finishes (the end screen when something
 * was shared, else Home).
 */
export function buildAction(facts: TActionFacts): TConfirmAction {
  if (!isSubmitted(facts.individual)) {
    return individualFirst(facts.individual);
  }
  const shareCustomerIds = idsToShare(facts);
  return { shareCustomerIds, then: nextStep(facts.pick, shareCustomerIds.length) };
}

/**
 * The label of the primary: Continue for a wizard, Share with provider, Finish it for a Draft (an
 * entity's or the Individual's).
 */
export function primaryLabelFor(then: TConfirmAction['then']): string {
  if (then === 'shared-end') {
    return SHARE_WITH_PROVIDER_LABEL;
  }
  if (then === 'nothing-to-start') {
    return NOTHING_TO_START_LABEL;
  }
  return typeof then === 'object' ? FINISH_IT_LABEL : CONTINUE_LABEL;
}

/**
 * The wizard or Home the unchanged screen reads (`TEntityPlan.target`); `action` supersedes it. A
 * share or nothing left both read as nothing to start.
 */
export function targetFor(then: TConfirmAction['then']): TEntityPlanTarget {
  if (then === 'shared-end') {
    return 'nothing-to-start';
  }
  if (typeof then === 'object') {
    return then.kind === 'resume-individual-draft' ? 'individual' : then.entityType;
  }
  return then;
}
